// =============================================================================
// WORD-GUIDANCE DETECTION — the alignment-essay panel's honest budget default.
// Jason's live-test finding on the real Anthropic application (2026-07-05):
// the application page ITSELF states the winning length ("successful answers
// are 200-400 words" is Anthropic's own form text) — "which is data we can
// see." So the panel should DERIVE its default word budget from the job's own
// ALREADY-SERVED text (JobRecord.description / .requirements — no new fetch,
// no scraping) when that guidance genuinely exists, and say so honestly.
//
// NO INVENTION applies to the guidance itself: this is a conservative regex
// scan over real, already-fetched text — it never infers or guesses a budget
// when the text doesn't state one. Absent guidance -> null, and the caller
// falls back to today's un-derived defaults with no claim made.
//
// PROVENANCE: the returned `quote` is the EXACT matched sentence fragment
// (trimmed, whitespace-collapsed) — the panel renders it verbatim, never
// paraphrased, so the user can see for themselves that this is real text from
// the listing, not an assistant's summary of it.
// =============================================================================

export interface WordGuidanceMatch {
  /** Lower bound of the stated range, or undefined for a ceiling-only
   *  phrasing ("no more than 400 words" has no stated minimum). */
  min?: number;
  /** Upper bound — always present when a match is found (a range's high end,
   *  or the ceiling itself for "no more than" / "under" / "or less"
   *  phrasings, or the approximate value for "approximately N words"). */
  max: number;
  /** The exact matched sentence fragment, verbatim from the source text —
   *  never paraphrased. Whitespace-collapsed for clean rendering, but no
   *  words added, removed, or reworded. */
  quote: string;
}

// Sanity bounds for what counts as a plausible WORD-COUNT guidance number —
// used to reject numbers that match the shape of a range/ceiling phrase but
// are actually something else entirely (a year range, a salary figure, a page
// count). 50-2000 comfortably spans every real "answer length" convention
// (a one-line answer through a multi-page essay) while excluding years
// (2020-2026), most salary bands stated in whole dollars (80000-120000), and
// abbreviated salary shorthand (80-120, meant as "$80-120k").
const MIN_PLAUSIBLE_WORDS = 50;
const MAX_PLAUSIBLE_WORDS = 2000;

/** True when a number is in the plausible word-count sanity range. */
function isPlausibleWordCount(n: number): boolean {
  return Number.isFinite(n) && n >= MIN_PLAUSIBLE_WORDS && n <= MAX_PLAUSIBLE_WORDS;
}

/** Numbers can carry thousands-separator commas ("1,000 words") — strip them
 *  before parsing so "1,000" parses as 1000, not truncating at the comma. */
function parseIntLoose(raw: string): number {
  return Number.parseInt(raw.replace(/,/g, ''), 10);
}

/** Collapse internal whitespace/newlines to single spaces and trim — the
 *  quote should read as one clean sentence fragment regardless of how the
 *  source HTML/text wrapped it. */
function cleanQuote(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Extract the sentence-ish fragment surrounding a regex match, for
 *  provenance display. Walks outward from the match to the nearest sentence
 *  boundary (., !, ?, or newline) on each side, capped at a generous length
 *  so a fragment from a single giant run-on paragraph doesn't balloon into
 *  the whole page. */
function extractQuote(text: string, matchStart: number, matchEnd: number): string {
  const CONTEXT_CAP = 220;
  const beforeSliceStart = Math.max(0, matchStart - CONTEXT_CAP);
  const before = text.slice(beforeSliceStart, matchStart);
  const after = text.slice(matchEnd, matchEnd + CONTEXT_CAP);

  const sentenceStartInBefore = Math.max(
    before.lastIndexOf('.'),
    before.lastIndexOf('!'),
    before.lastIndexOf('?'),
    before.lastIndexOf('\n'),
  );
  // sentenceStartInBefore is an index WITHIN `before` — translate back to an
  // absolute index in `text` by adding beforeSliceStart, then step past the
  // boundary character itself (+1) so the quote starts at the next sentence,
  // not the trailing punctuation of the prior one.
  const start = sentenceStartInBefore >= 0 ? beforeSliceStart + sentenceStartInBefore + 1 : beforeSliceStart;

  const sentenceEndInAfter = after.search(/[.!?\n]/);
  const end = sentenceEndInAfter >= 0 ? matchEnd + sentenceEndInAfter + 1 : matchEnd + after.length;

  return cleanQuote(text.slice(Math.max(0, start), Math.min(text.length, end)));
}

interface PatternDefinition {
  name: string;
  regex: RegExp;
  /** Extracts { min?, max } from a successful match, or null to reject this
   *  particular match (e.g. numbers outside the plausible sanity range). */
  extract: (match: RegExpMatchArray) => { min?: number; max: number } | null;
}

// Shared numeric sub-pattern: 2-5 bare digits ("200", "12345"), OR a
// thousands-grouped number whose leading group is 1-3 digits ("1,000",
// "20,000"). Captured as ONE group so `m[n]` is always the whole number
// string (commas intact — parseIntLoose strips them at parse time). Written
// with the grouped alternative FIRST so the regex engine tries the (rarer,
// more specific) comma form before falling back to the bare-digit form.
const NUM = String.raw`\d{1,3}(?:,\d{3})+|\d{2,5}`;

// Word-boundary-safe patterns for real "answer length" guidance shapes. Every
// pattern requires the literal word "word"/"words" adjacent to the number(s)
// so a bare numeric range in the text (a year span, a salary band, a page
// count without the word "words") never matches — the word "words" itself is
// the anti-invention guardrail here, not just the numeric sanity range.
const PATTERNS: PatternDefinition[] = [
  {
    // "200-400 words", "200–400 words", "200—400 words", "200 to 400 words"
    name: 'range',
    regex: new RegExp(String.raw`\b(${NUM})\s*(?:-|–|—|to)\s*(${NUM})\s*words?\b`, 'gi'),
    extract: (m) => {
      const min = parseIntLoose(m[1]);
      const max = parseIntLoose(m[2]);
      if (!isPlausibleWordCount(min) || !isPlausibleWordCount(max)) return null;
      if (min > max) return null;
      return { min, max };
    },
  },
  {
    // "no more than 400 words", "no more than 400 words long"
    name: 'no_more_than',
    regex: new RegExp(String.raw`\bno more than\s*(${NUM})\s*words?\b`, 'gi'),
    extract: (m) => {
      const max = parseIntLoose(m[1]);
      if (!isPlausibleWordCount(max)) return null;
      return { max };
    },
  },
  {
    // "under 400 words"
    name: 'under',
    regex: new RegExp(String.raw`\bunder\s*(${NUM})\s*words?\b`, 'gi'),
    extract: (m) => {
      const max = parseIntLoose(m[1]);
      if (!isPlausibleWordCount(max)) return null;
      return { max };
    },
  },
  {
    // "in 400 words or less", "in 400 words or fewer"
    name: 'or_less',
    regex: new RegExp(String.raw`\bin\s*(${NUM})\s*words?\s*or\s*(?:less|fewer)\b`, 'gi'),
    extract: (m) => {
      const max = parseIntLoose(m[1]);
      if (!isPlausibleWordCount(max)) return null;
      return { max };
    },
  },
  {
    // "approximately 300 words", "approx. 300 words", "~300 words"
    name: 'approximately',
    regex: new RegExp(String.raw`\b(?:approximately|approx\.?|~)\s*(${NUM})\s*words?\b`, 'gi'),
    extract: (m) => {
      const max = parseIntLoose(m[1]);
      if (!isPlausibleWordCount(max)) return null;
      return { max };
    },
  },
];

/**
 * Scan already-served job text for a genuine word-count guidance statement.
 * Conservative by design: every pattern requires the literal word
 * "word"/"words" beside the number(s), plus a 50-2000 sanity range, so a year
 * span ("2020-2026"), a salary band ("$80-120k" / "80,000-120,000"), or a
 * page count ("2-4 pages") never false-positives as word-count guidance.
 *
 * Returns the FIRST plausible match across the supplied text sections (checked
 * in the order given — description first, then any details fields), or null
 * when nothing genuine is found. Never invents a budget: absence is honestly
 * reported as null, never a guessed default.
 */
export function detectWordGuidance(text: string | null | undefined): WordGuidanceMatch | null {
  if (!text || !text.trim()) return null;

  for (const pattern of PATTERNS) {
    // Fresh regex instance per scan (the module-level regex objects carry
    // the `g` flag and therefore mutable lastIndex state — a shared instance
    // reused across calls would silently skip matches on a second call).
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
    const match = regex.exec(text);
    if (!match || match.index === undefined) continue;

    const extracted = pattern.extract(match);
    if (!extracted) continue;

    const quote = extractQuote(text, match.index, match.index + match[0].length);
    return { min: extracted.min, max: extracted.max, quote };
  }

  return null;
}

/**
 * Scan multiple already-served text sections in priority order (e.g.
 * description before requirements) and return the first genuine match.
 * Thin convenience wrapper over detectWordGuidance for the panel's actual
 * call site, which has more than one field in scope.
 */
export function detectWordGuidanceAcross(sections: (string | null | undefined)[]): WordGuidanceMatch | null {
  for (const section of sections) {
    const found = detectWordGuidance(section);
    if (found) return found;
  }
  return null;
}

export interface DerivedBudget {
  target: number;
  max: number;
}

/**
 * Derive a { target, max } budget preset from a genuine detected match.
 *
 * CHOICE (documented per the build brief): target = the MIDPOINT of the
 * match's own range (or the match's max itself when no min was stated, e.g.
 * a bare "no more than 400 words" ceiling has nothing to average against).
 * Midpoint over "upper-third of the span" because the match's max is already
 * a HARD ceiling enforced in code (BrevityService's own compress-on-over-max
 * pass) — biasing the target toward that ceiling would systematically push
 * more drafts into the compression path for no benefit, when the company's
 * own stated range already reads as "anywhere in here is a successful
 * answer," not "aim high within it." The midpoint is also the simplest,
 * most defensible reading of a stated range with no other signal to weight
 * it — same posture as this app's other "no invented tiebreak" defaults.
 *
 * max = the match's own stated max, verbatim (never widened or narrowed —
 * this IS the number the company published).
 *
 * Rounds the target to the nearest multiple of 5 purely for a clean stepper
 * default (the UI's steppers move in 25s, but the raw midpoint of an odd
 * range like 200-401 would otherwise land on a value like 300.5) — rounding
 * never changes which underlying match was detected or what max is enforced.
 */
export function deriveBudgetFromMatch(match: WordGuidanceMatch): DerivedBudget {
  const rawTarget = match.min !== undefined ? (match.min + match.max) / 2 : match.max;
  const target = Math.round(rawTarget / 5) * 5;
  return { target, max: match.max };
}
